import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Button,
} from '@mui/material';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import GasMeterIcon from '@mui/icons-material/GasMeter';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { useAuth } from '../../contexts/AuthContext';

const EnergyManagement = () => {
  const { currentSiteId } = useAuth();
  const [mpxnInput, setMpxnInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sampleSnippet, setSampleSnippet] = useState('');
  const [moveInDate, setMoveInDate] = useState('');
  const energyData = [
    { type: 'Electricity', usage: '2,450 kWh', cost: '$420', change: -8.2, color: '#3b82f6', icon: <ElectricBoltIcon /> },
    { type: 'Natural Gas', usage: '1,200 kWh', cost: '$180', change: -5.1, color: '#ef4444', icon: <GasMeterIcon /> },
    { type: 'Renewable', usage: '450 kWh', cost: '$45', change: 25.3, color: '#10b981', icon: <SolarPowerIcon /> },
  ];

  const recommendations = [
    { title: 'Install LED Lighting', impact: 'Save 15% on electricity', priority: 'High' },
    { title: 'Optimize HVAC Schedule', impact: 'Reduce 20% heating/cooling', priority: 'Medium' },
    { title: 'Add Solar Panels', impact: 'Generate 30% renewable energy', priority: 'Low' },
    { title: 'Smart Thermostats', impact: 'Save 12% on HVAC', priority: 'High' },
  ];

  const handleRegisterAndTest = async () => {
    setError('');
    setSuccessMessage('');
    setSampleSnippet('');

    if (!currentSiteId) {
      setError('No site selected. Please select a site from the Sites page.');
      return;
    }
    const trimmed = (mpxnInput || '').trim();
    if (!trimmed) {
      setError('Please enter an MPxN from your energy bill.');
      return;
    }

    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';

      // 1. Register trusted consent for this MPxN using the same payload
      // shape as the interview app.
      const today = new Date().toISOString().slice(0, 10); // yyyy-MM-dd
      const consentRes = await fetch(
        `${baseUrl}/api/energy/n3rgy/${currentSiteId}/consents/add-trusted-consent`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mpxn: trimmed,
            moveInDate: moveInDate || today,
            evidence: 'RTN dashboard trusted consent',
            getHistoryData: 'false',
            electricityImportUpdateFrequency: 'daily',
            electricityExportUpdateFrequency: 'never',
            gasUpdateFrequency: 'daily',
          }),
        }
      );

      const consentBody = await consentRes.json().catch(() => ({}));
      if (!consentRes.ok) {
        throw new Error(consentBody.message || 'Failed to register trusted consent with n3rgy');
      }

      // 2. Fetch basic MPxN details to prove it works
      const detailsRes = await fetch(
        `${baseUrl}/api/energy/n3rgy/${currentSiteId}/mpxn/${encodeURIComponent(trimmed)}`
      );
      const detailsBody = await detailsRes.json().catch(() => ({}));
      if (!detailsRes.ok) {
        throw new Error(detailsBody.message || 'Consent registered but failed to fetch MPxN details');
      }

      setSuccessMessage(`MPxN ${trimmed} registered and basic details loaded successfully.`);
      setSampleSnippet(JSON.stringify(detailsBody, null, 2).slice(0, 600));

      // Persist MPxN against this site so it is available for future syncs
      try {
        await fetch(`${baseUrl}/api/sites/${currentSiteId}/energy-mpxn`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mpxn: trimmed, mpxnMoveInDate: moveInDate || today }),
        });
      } catch (e) {
        // non-fatal
      }
    } catch (e) {
      setError(e.message || 'Failed to register MPxN or fetch data from n3rgy.');
    } finally {
      setLoading(false);
    }
  };

  // On mount / site change: load stored MPxN and, if present, try a quick sync
  useEffect(() => {
    const init = async () => {
      if (!currentSiteId) return;
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
        // Fetch stored MPxN for this site
        const res = await fetch(`${baseUrl}/api/sites/${currentSiteId}/energy-mpxn`);
        const body = await res.json().catch(() => ({}));
        if (res.ok && body.mpxn) {
          setMpxnInput(body.mpxn);
        }
        if (res.ok && body.mpxnMoveInDate) {
          setMoveInDate(body.mpxnMoveInDate);
        }
      } catch (e) {
        // ignore init errors
      }
    };
    init();
  }, [currentSiteId]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Energy Management
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Monitor and optimize your energy consumption patterns and costs.
      </Typography>

      {/* Smart Meter / n3rgy Connection */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ 
              backgroundColor: 'primary.light', 
              borderRadius: '50%', 
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <ElectricBoltIcon sx={{ color: 'primary.contrastText' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight="bold">
                Connect your smart meter (n3rgy)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the MPxN for this site to register trusted consent with n3rgy using RTN&apos;s API key, then we&apos;ll pull a sample of meter data to confirm it&apos;s working.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <TextField
                    label="Site MPxN"
                    placeholder="e.g. 1234567890123"
                    size="small"
                    value={mpxnInput}
                    onChange={(e) => setMpxnInput(e.target.value)}
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    label="Move-in date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={moveInDate}
                    onChange={(e) => setMoveInDate(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRegisterAndTest}
                    disabled={loading}
                    sx={{ flex: 1, minWidth: 160 }}
                  >
                    {loading ? 'Connecting…' : 'Register & Test'}
                  </Button>
                </Box>
              </Box>
              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
              {successMessage && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  {successMessage}
                </Typography>
              )}
              {sampleSnippet && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: 'grey.900',
                    color: 'grey.100',
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {sampleSnippet}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* Energy Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {energyData.map((energy, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card sx={{ height: '100%', borderTop: `4px solid ${energy.color}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ 
                    backgroundColor: `${energy.color}20`, 
                    borderRadius: '50%', 
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(energy.icon, { sx: { color: energy.color, fontSize: 24 } })}
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {energy.type}
                  </Typography>
                </Box>
                
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  {energy.usage}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Cost
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {energy.cost}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {energy.change >= 0 ? (
                    <TrendingUpIcon sx={{ color: '#ef4444' }} />
                  ) : (
                    <TrendingDownIcon sx={{ color: '#10b981' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: energy.change >= 0 ? '#ef4444' : '#10b981', fontWeight: 'bold' }}
                  >
                    {energy.change >= 0 ? '+' : ''}{energy.change}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    vs last month
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Total Energy Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Total Energy Consumption
                  </Typography>
                  <Typography variant="h2" fontWeight="bold" color="primary">
                    4,100 kWh
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Combined electricity and gas usage
                  </Typography>
                </Box>
                <Chip 
                  label="Efficient" 
                  color="success" 
                  icon={<EnergySavingsLeafIcon />}
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Renewable Energy</Typography>
                  <Typography variant="body2" fontWeight="bold">11%</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={11} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: '#e5e7eb',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #10b981, #34d399)',
                      borderRadius: 5,
                    }
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingDownIcon sx={{ color: '#10b981', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" color="#10b981">
                    -7.2% overall reduction this month
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Saving approximately $85 compared to last month
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Peak Usage Times
              </Typography>
              <List>
                {[
                  { time: '6:00-9:00 AM', usage: 'High', icon: <WarningIcon color="warning" /> },
                  { time: '12:00-2:00 PM', usage: 'Very High', icon: <WarningIcon color="error" /> },
                  { time: '6:00-9:00 PM', usage: 'High', icon: <WarningIcon color="warning" /> },
                  { time: '10:00 PM-5:00 AM', usage: 'Low', icon: <CheckCircleIcon color="success" /> },
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.time}
                      secondary={`Usage: ${item.usage}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fef3c7', borderRadius: 2 }}>
                <Typography variant="body2" color="#92400e" fontWeight="medium">
                  💡 Recommendation
                </Typography>
                <Typography variant="caption" color="#92400e">
                  Shift non-essential operations to off-peak hours
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recommendations */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Energy Optimization Recommendations
          </Typography>
          <Grid container spacing={2}>
            {recommendations.map((rec, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6">{rec.title}</Typography>
                      <Chip 
                        label={rec.priority} 
                        size="small"
                        color={rec.priority === 'High' ? 'error' : rec.priority === 'Medium' ? 'warning' : 'success'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {rec.impact}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Estimated savings: $120-180/month
                      </Typography>
                      <Chip 
                        label="Apply" 
                        size="small" 
                        color="primary"
                        variant="outlined"
                        clickable
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EnergyManagement;